'use client';

import { useCallback, useState } from 'react';
import type { ApolloError } from '@apollo/client';
import type { FireflyTranslateLocaleCodeInput } from '@frameio/graphql-client/generated/types';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing/getLink';
import { useTrack } from '@frameio/shared/segment/hooks';
import {
  Button2,
  Dialog,
  InlineButton,
  ScrollContainer,
  Text,
  TextField,
  theme,
  ToggleCheckbox,
} from '@frameio/vapor';
import { IconMagnifyingglass } from '@frameio/vapor-icons';
import { DialogFooter } from '../components/DialogFooter';
import { DialogHeader } from '../components/DialogHeader';
import { Divider } from '../components/Divider';
import { getTranslateVideoLanguageOptions } from '../constants/dubLanguages';
import { SHARED_TEXT } from '../constants/sharedText';
import { useCreateTranslateOperationWithConsentMutation } from '../gql/mutations/generated/CreateTranslateOperation';
import { useFireflyErrorToast } from '../hooks/useFireflyErrorToast';
import { useFireflyRateLimit } from '../hooks/useFireflyRateLimit';
import { useVariationsProcessingToast } from '../hooks/useVariationsProcessingToast';
import styles from './FireflyTranslateDialog.module.css';
import TEXT from './generated/TEXT/FireflyTranslateDialog';

TEXT.DEFINE({
  /** Name of the translate video action. */
  actionName: 'Translate Video',
  /** Subtitle of the translate video dialog. */
  subtitle: 'Translate video into other languages.',
  /** Search input placeholder */
  search: 'Search',
  /** Languages the user wants to translate the video into. */
  outputLanguages: 'Output Languages',
  /** Restrictions on the translate video feature. */
  infoText:
    'This feature only supports H265/H264 video files up to 30 minutes long.',
  /** Submit button text. */
  generate: 'Generate Variations',
  /** Cancel button text. */
  cancel: 'Cancel',
  /** This text is used to close the dialog. */
  dialogCloseLabel: 'Close Dialog',
});

const INFO_TEXT_HREF =
  'https://developer.adobe.com/audio-video-firefly-services/getting_started/usage/';

const MAX_ASSETS_LIMIT_TRANSLATE_VIDEO = 100;

type FireflyTranslateDialogProps = {
  assetIds?: string[];
  consentTimestamp?: string;
  isShowing?: boolean;
  onClose?: () => void;
};

export const FireflyTranslateDialog = ({
  assetIds = [],
  consentTimestamp,
  isShowing = false,
  onClose,
}: FireflyTranslateDialogProps) => {
  const [query, setQuery] = useState('');
  const [selectedLanguageCodes, setSelectedLanguageCodes] = useState<
    FireflyTranslateLocaleCodeInput[]
  >([]);
  const track = useTrack();
  const [createTranslateOperationWithConsent, { loading, called }] =
    useCreateTranslateOperationWithConsentMutation();
  const projectId = useActiveProjectId();
  const showVariationsProcessingToast = useVariationsProcessingToast();
  const showErrorToast = useFireflyErrorToast();
  const { handleRateLimitError, rateLimitDialog } = useFireflyRateLimit();

  const tooManySelectedMessage = SHARED_TEXT.tooManySelectedText({
    actionName: TEXT.actionName,
    assetCount: MAX_ASSETS_LIMIT_TRANSLATE_VIDEO,
  });
  const startErrorMessage = SHARED_TEXT.errorMessageToast({
    actionName: TEXT.actionName,
  });

  const languages = getTranslateVideoLanguageOptions().filter((opt) =>
    opt.label.toLowerCase().includes(query.toLowerCase())
  );

  const toggleLanguage = useCallback(
    (code: FireflyTranslateLocaleCodeInput) => {
      setSelectedLanguageCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
      );
    },
    []
  );

  const handleGenerate = useCallback(async () => {
    if (selectedLanguageCodes.length === 0) return;
    if (assetIds.length > MAX_ASSETS_LIMIT_TRANSLATE_VIDEO) {
      showErrorToast(tooManySelectedMessage);
      return;
    }
    const hasConsented = !!consentTimestamp;
    try {
      const { errors, data } = await createTranslateOperationWithConsent({
        variables: {
          input: {
            sourceAssetIds: assetIds,
            targetLanguages: selectedLanguageCodes,
            lipSync: false,
            userConsent: {
              timestamp: consentTimestamp ?? '',
              hasConsented,
              languageCode: 'en-US',
            },
          },
        },
      });
      if (errors?.length) {
        if (!handleRateLimitError(errors)) {
          showErrorToast(startErrorMessage);
        }
        return;
      }
      track('generation-started', {
        action_name: 'Translate Video',
        number_of_assets: assetIds.length,
        number_of_languages: selectedLanguageCodes.length,
      });
      const destinationId =
        data?.createTranslateOperationWithConsent?.fireflyOperation?.destination
          ?.id;
      const href = projectId
        ? getLink({ page: 'folder', projectId, folderAssetId: destinationId })
        : null;
      if (href) {
        showVariationsProcessingToast({ href, projectId });
      }
      onClose?.();
    } catch (e) {
      if (!handleRateLimitError(e as ApolloError)) {
        showErrorToast(startErrorMessage);
      }
    }
  }, [
    assetIds,
    consentTimestamp,
    createTranslateOperationWithConsent,
    onClose,
    projectId,
    selectedLanguageCodes,
    showVariationsProcessingToast,
    showErrorToast,
    handleRateLimitError,
    track,
    tooManySelectedMessage,
    startErrorMessage,
  ]);

  const handleClose = useCallback(() => {
    if (!called) {
      track('generation-dismissed', {
        action_name: 'Translate Video',
        number_of_assets: assetIds.length,
        number_of_languages: selectedLanguageCodes.length,
      });
    }
    onClose?.();
  }, [called, track, assetIds.length, selectedLanguageCodes.length, onClose]);

  return (
    <Dialog
      enableClickOutsideToClose={false}
      isShowing={isShowing}
      onRequestClose={handleClose}
      size="small"
    >
      <DialogHeader
        closeLabel={TEXT.dialogCloseLabel}
        onClose={handleClose}
        subtitle={TEXT.subtitle}
        title={TEXT.actionName}
      />

      <Divider />

      <div className={styles.body}>
        <div className={styles.searchGroup}>
          <Text color="body" fontWeight="regular" variant="body">
            {TEXT.outputLanguages}
          </Text>
          <TextField
            adornmentBefore={
              <IconMagnifyingglass
                color={theme.colors.icon.primary}
                size={16}
              />
            }
            onChange={(e) => setQuery(e.target.value)}
            placeholder={TEXT.search}
            value={query}
          />
        </div>

        <ScrollContainer
          enableBottomBorder={true}
          enableTopShadow={true}
          height="242px"
          paddingX={16}
          paddingY={8}
          shadowSize="medium"
        >
          <div className={styles.languageList}>
            {languages.map((opt) => (
              <button
                key={opt.code}
                className={styles.languageRow}
                data-testid={`language-row-${opt.code}`}
                onClick={() => toggleLanguage(opt.code)}
                type="button"
              >
                <ToggleCheckbox
                  checked={selectedLanguageCodes.includes(opt.code)}
                />
                <Text color="primary" variant="body">
                  {opt.label}
                </Text>
              </button>
            ))}
          </div>
        </ScrollContainer>

        <div className={styles.infoText}>
          <Text color="secondary" fontWeight="regular" variant="caption">
            {TEXT.infoText}{' '}
            <InlineButton
              onPress={() =>
                window.open(INFO_TEXT_HREF, '_blank', 'noopener,noreferrer')
              }
            >
              <Text color="link" variant="linkSmall">
                Learn more
              </Text>
            </InlineButton>
          </Text>
        </div>
      </div>

      <Divider />
      <DialogFooter>
        <Button2 onPress={handleClose} variant="secondary">
          {TEXT.cancel}
        </Button2>
        <Button2
          isDisabled={selectedLanguageCodes.length === 0 || loading}
          isLoading={loading}
          onPress={handleGenerate}
          variant="primary"
        >
          {TEXT.generate}
        </Button2>
      </DialogFooter>
      {rateLimitDialog}
    </Dialog>
  );
};
